import React, { useMemo, useCallback } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import styles from "./charts.css";
import { generateColors } from "./colors";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip as AntdTooltip } from "antd";
import { Empty } from "antd";

const {
  ["chart-container"]: chartContainerClassName,
  ["chart-title"]: chartTitleClassName,
  ["chart-wrapper"]: chartWrapperClassName,
  ["donut-chart-total-text"]: donutChartTotalTextClassName,
  ["donut-chart-total-label"]: donutChartTotalLabelClassName,
  ["legend-container"]: legendContainerClassName,
  ["legend"]: legendClassName,
  ["legend-item"]: legendItemClassName,
  ["legend-item-color"]: legendItemColorClassName,
} = styles;

type DataItem = {
  field: string;
  count: number;
};

interface DonutChartProps {
  data: DataItem[];
  title?: string;
  explanatoryText?: string;
  width?: string | number;
  height?: number;
}

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title = "Distribution Chart",
  explanatoryText,
  width = "100%",
  height = 300,
}) => {
  const total = useMemo(() => data.reduce((sum, entry) => sum + entry.count, 0), [data]);

  const colors = useMemo(() => generateColors(data.length), [data.length]);

  const renderCustomizedLabel = useCallback(
    (props: any) => {
      const { cx, cy } = props;
      return (
        <g>
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            fill="#000"
            className={donutChartTotalTextClassName}
          >
            {total}
          </text>
          <text
            x={cx}
            y={cy}
            dy="1.5em"
            textAnchor="middle"
            fill="#000"
            className={donutChartTotalLabelClassName}
          >
            Total
          </text>
        </g>
      );
    },
    [total]
  );

  const tooltipFormatter = useCallback(
    (value: number, name: string) => {
      const percentage = ((value / total) * 100).toFixed(0);
      return [`${value} (${percentage}%)`, name];
    },
    [total]
  );

  if (data.length === 0) {
    return (
      <div className={chartContainerClassName} style={{ width, height }}>
        <h2 className={chartTitleClassName}>
          {title}
          {explanatoryText && (
            <AntdTooltip placement="top" title={explanatoryText}>
              <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
            </AntdTooltip>
          )}
        </h2>
        <div
          className={chartWrapperClassName}
          style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <Empty description="No data" />
        </div>
      </div>
    );
  }

  return (
    <div className={chartContainerClassName} style={{ width }}>
      <h2 className={chartTitleClassName}>
        {title}{" "}
        {explanatoryText && (
          <AntdTooltip placement="top" title={explanatoryText}>
            <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
          </AntdTooltip>
        )}
      </h2>
      <div className={chartWrapperClassName} style={{ height: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="80%"
              paddingAngle={5}
              dataKey="count"
              nameKey="field"
              animationBegin={100}
              animationDuration={800}
              label={renderCustomizedLabel}
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index]} />
              ))}
            </Pie>
            <Tooltip formatter={tooltipFormatter} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className={legendContainerClassName}>
        <div className={legendClassName}>
          {data.map((entry, index) => (
            <div key={index} className={legendItemClassName}>
              <div
                className={legendItemColorClassName}
                style={{
                  backgroundColor: colors[index],
                }}
              />
              <span>{entry.field}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
